import { RouterProvider } from "react-router-dom";
import { appRouter } from "./Infrastructure/Routing/AppRouter";
import { AuthenticatedLayoutContext } from "Shared/Layouts/AuthenticatedLayout";
import { ThemeProvider } from "@emotion/react";
import { ScopedCssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { processSignOut } from "Auth/Hooks/useSignOut";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import { setupClient } from "Infrastructure/Api/Api";
import { getTheme } from "Infrastructure/Themes/Themes";
import { CzechResourcesDictionary } from "Infrastructure/Translations/CzechResourcesDictionary";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import { trackUserIdentification } from "Infrastructure/Utils/TrackingUtils";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { zodI18nMap } from "zod-i18n-map";
import i18n from "i18next";
import DialogProvider from "Infrastructure/Providers/DialogProvider/DialogProvider";
import SnackbarProvider from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";

function App() {
	initSentry();

	const theme = getTheme();
	i18n.use(initReactI18next).init({
		resources: {
			cs: CzechResourcesDictionary,
		},
		lng: "cs",
		fallbackLng: "cs",
		interpolation: {
			escapeValue: false,
		},
	});

	z.setErrorMap(zodI18nMap);

	setupClient({
		apiUrl: () => import.meta.env.VITE_API_URL,
		jwtKey: JWT_KEY,
		responseMiddlewares: [
			{
				name: "auth",
				fn: (r) => {
					if (r.status === 401 && localStorage.getItem(JWT_KEY) !== null) {
						processSignOut(queryClient);
					} else {
						trackUserIdentification();
					}
					return r;
				},
			},
		],
	});

	const queryClient = new QueryClient();

	return (
		<QueryClientProvider client={queryClient}>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
			<ThemeProvider theme={theme}>
				<ScopedCssBaseline>
					<SnackbarProvider>
						<DialogProvider>
							<AuthenticatedLayoutContext.Provider
								value={{ isMenuShown: true }}
							>
								<RouterProvider router={appRouter} />
							</AuthenticatedLayoutContext.Provider>
						</DialogProvider>
					</SnackbarProvider>
				</ScopedCssBaseline>
			</ThemeProvider>
		</QueryClientProvider>
	);
}

export default App;
